import React from "react";
// Material UI
import { TextField, Box, Typography, Paper } from '@mui/material';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";

// Interface
import { ITask } from "../../interface/task";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { changeValue } from "../../redux/actions/index";

// --- Styled Components (Identiques à TaskDetails) ---

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f8fafc", // Même gris bleuté
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "2px solid #e2e8f0",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "0.85rem",
    padding: "8px",
    fontWeight: 600,
    color: "#1e293b"
  },
}));

const InputField = styled(TextField)({
  '& .MuiFilledInput-root': {
    backgroundColor: 'transparent',
    '&:before, &:after': { display: 'none' },
    borderRadius: '4px',
  },
  '& input': {
    padding: '8px !important',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: "#1e293b"
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: "#1e293b !important", // Pour que le texte reste lisible même désactivé
  }
});

const LotCosts = ({ lot }: { lot: string }) => {
  const tasksCharges = useSelector((state: RootState) => state.lotSynthesis.tasksCharges);
  const totalCharges = useSelector((state: RootState) => state.lotSynthesis.totalCharges);
  const PPR = useSelector((state: RootState) => state.lotSynthesis.PPR);
  const lotCosts = useSelector((state: RootState) => state.lotSynthesis.lotCosts);
  const total = useSelector((state: RootState) => state.lotSynthesis.total);

  const dispatch = useDispatch();

  const displayCell = (cost: any, displayAdornments = false) =>
    Object.keys(cost).map((key) => {
      let element = cost[key];
      if (key !== "name" && key !== "description" && key !== "_id") {
        return (
          <StyledTableCell key={key} sx={{ p: 0.5 }}>
            <InputField
              fullWidth
              variant="filled"
              disabled
              value={(displayAdornments && element) ? element + " €" : element ? element : 0}
            />
          </StyledTableCell>
        );
      }
      return null;
    });

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
        Synthèse des coûts et charges
      </Typography>
      
      <TableContainer component={Paper} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none' }}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ minWidth: 175 }}>Description</StyledTableCell>
              {["Concept.", "Real/Dev", "Real/Infra", "Qual/Stra", "Qual/CCT", "Qual/Exec", "Rec/Stra", "Rec/CCT", "Rec/Exec", "Deploy.", "Pil.&C.", "Acc", "Guar."].map((h) => (
                <StyledTableCell key={h} align="center">{h}</StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow hover>
              <StyledTableCell sx={{ bgcolor: '#f1f5f9' }}>Charge Calculée</StyledTableCell>
              {displayCell(tasksCharges)}
            </TableRow>
            <TableRow hover>
              <StyledTableCell sx={{ bgcolor: '#f1f5f9' }}>Marge pour Risque</StyledTableCell>
              {displayCell(PPR)}
            </TableRow>
            <TableRow hover>
              <StyledTableCell sx={{ bgcolor: '#f1f5f9', fontWeight: 800 }}>Charge Totale</StyledTableCell>
              {displayCell(totalCharges)}
            </TableRow>
            <TableRow hover>
              <StyledTableCell sx={{ bgcolor: '#f8fafc', color: '#3b82f6' }}>Coût par Profil</StyledTableCell>
              {displayCell(lotCosts, true)}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Section Coût Total - Design Impactant */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mt: 3 
      }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: "16px 32px", 
            
            color: 'white', 
            borderRadius: '16px',
            border: '1px solid #334155',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            minWidth: '250px'
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              textTransform: 'uppercase', 
              letterSpacing: '0.1em', 
              fontWeight: 700,
              color: '#94a3b8', // Gris bleu clair
              mb: 0.5
            }}
          >
            Montant Total du Lot
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 400, 
                color: '#3b82f6', // Bleu vif pour le chiffre
                lineHeight: 1
              }}
            >
              {total?.toLocaleString('fr-FR')}
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: '#3b82f6',
                opacity: 0.8 
              }}
            >
              €
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LotCosts;